import { withoutSmsForStatements } from "@areas/trade-accounts/flows/BatchTemplateRequestModal/utils/buildSuggestionRequest";
import SuggestionIcon from "./SuggestionIcon";
import { paymentSummary } from "./suggestedPaymentCopy";
import { cancellationKind, cancellationText } from "./cancellationCopy";
import { sendingToContacts } from "./actionAmendments";
import { isActionBacked, isCancellation, isSendEmailType } from "./DraftSuggestionCard";

// Mirrors DraftSuggestionCard's label map — the note is the only type whose card label differs from the
// stored signal label; everything else keeps the label the server sent.
const ACTION_LABELS = {
  CustomerInteractionNoted: "Add note",
};

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

// The body line for a non-payment signal — the same shape DraftSuggestionCard derives, so a reopened card
// reads identically. Payment signals take their line from paymentSummary(payment) instead.
function bodyText(type, params, customer, config) {
  if (type === "CustomerInteractionNoted") return params?.summary ?? "";
  if (isActionBacked(type)) return params?.explanation ?? "";
  if (isSendEmailType(type)) {
    const to = [config?.emailEnabled && customer?.email, config?.smsEnabled && customer?.phone]
      .filter(Boolean)
      .join(" & ");
    const suffix = to ? ` to ${to}` : "";
    const invoiceNums = toArray(params?.invoiceNumbers);
    const salesOrderNums = toArray(params?.salesOrderNumbers);
    const invoices = invoiceNums.length ? ` for invoice ${invoiceNums.join(", ")}` : "";
    const salesOrders = salesOrderNums.length ? ` for sales order ${salesOrderNums.join(", ")}` : "";
    const document = params?.documentType === "Invoice" ? "invoice" : "proof of delivery";
    return `Send ${document}${invoices || salesOrders}${suffix}`;
  }
  return "";
}

// A reopened thread's suggestion, read-only. Visually the same card the live turn showed — gradient tile,
// action label, the resolved description (amount / target / schedule for a payment, via the same copy the
// live card uses) and recipients — but muted and without the accept / reject / edit controls, because the
// live action ids aren't stored so the suggestion can't be acted on again. To act, the user types a new turn.
export default function HistoricalSuggestionCard({ suggestion, customer }) {
  const isNote = suggestion.type === "CustomerInteractionNoted";
  const isCancel = isCancellation(suggestion.type);
  const config = withoutSmsForStatements(suggestion.params?.requestConfig);

  const action = isCancel
    ? `Cancel ${cancellationKind(suggestion.params)}`
    : (ACTION_LABELS[suggestion.type] ?? suggestion.label);

  const baseBody = isCancel
    ? (suggestion.params?.cardSummary ?? cancellationText(suggestion.params))
    : bodyText(suggestion.type, suggestion.params, customer, config);
  const body = paymentSummary(suggestion.payment) ?? baseBody;

  const sendingTo = sendingToContacts(suggestion.params);
  const sendingToText = sendingTo
    ? `Sending to: ${[
        sendingTo.email,
        sendingTo.phone,
        sendingTo.cc ? `Cc: ${sendingTo.cc}` : null,
        sendingTo.bcc ? `Bcc: ${sendingTo.bcc}` : null,
      ]
        .filter(Boolean)
        .join(" · ")}`
    : null;

  return (
    <div className="relative flex flex-col gap-4 rounded-2xl border border-zinc-200 bg-zinc-50/60 pt-6 px-4 pb-4">
      <div className="flex items-center gap-3">
        <span className="shrink-0 rounded-xl bg-[linear-gradient(135deg,#ec4899,#a855f7,#3b82f6,#22d3ee,#fb923c)] p-px opacity-80">
          <span className="flex size-9 items-center justify-center rounded-[11px] bg-white">
            <SuggestionIcon type={suggestion.type} className="size-5" />
          </span>
        </span>

        <div className="min-w-0 flex-1">
          <div className="text-xs text-zinc-500">{action}</div>
          {body && (
            <div className={isNote ? "mt-0.5 text-sm text-zinc-700" : "mt-0.5 text-sm text-zinc-600"}>
              {isNote ? `“${body}”` : body}
            </div>
          )}
          {sendingToText && <div className="mt-1 text-xs text-zinc-400">{sendingToText}</div>}
        </div>

        <span className="shrink-0 self-start text-[11px] font-medium uppercase tracking-wide text-zinc-300">
          Suggested
        </span>
      </div>
    </div>
  );
}
